import { IconButton } from '@atoms/IconButton';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemTextProps,
  Popover,
  Stack as MuiStack,
  alpha,
  styled,
} from '@mui/material';
import { NavItemProps } from '@organisms/Sidebar/Sidebar.types';
import { bgBlur } from '@theme/utils';
import { DefaultSidebarWidth, MiniSidebarWidth } from './config-sidebar';

type StyledSidebarContainerProps = {
  isMini?: boolean;
};

//** Sidebar Styles */
export const SidebarContainer = styled(MuiStack, {
  shouldForwardProp: (prop) => prop !== 'isMini',
})<StyledSidebarContainerProps>(({ isMini, theme }) => ({
  position: 'fixed',
  top: '0',
  left: '0',
  maxWidth: isMini ? MiniSidebarWidth : DefaultSidebarWidth,
  width: '100%',
  height: '100%',
  padding: theme.spacing(0, 1),
  borderRight: `1px dashed ${theme.palette.divider}`,
  transition: 'all ease 0.2s 0s',
  backgroundColor: theme.palette.common.white,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
    msOverflowStyle: 'none' /* 인터넷 익스플로러 */,
    scrollbarWidth: 'none' /* 파이어폭스 */,
  },
}));

export const SidebarMiniBtn = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isMini',
})<StyledSidebarContainerProps>(({ isMini, theme }) => ({
  position: 'fixed',
  top: '25px',
  left: isMini
    ? `calc(${MiniSidebarWidth} - 14px)`
    : `calc(${DefaultSidebarWidth} - 14px)`,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common.white,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: '50%',
  cursor: 'pointer',
  fill: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  transform: isMini ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: 'all ease 0.2s 0s',
  zIndex: 10,
}));

//** SideItem Styles */

type StyledDotIconProps = {
  active?: boolean;
};

export const StyledDotIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledDotIconProps>(({ active, theme }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: theme.palette.text.disabled,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
  }),

  ...(active && {
    transform: 'scale(2)',
    backgroundColor: theme.palette.primary.main,
  }),
}));

type StyledItemProps = Omit<NavItemProps, 'item'> & {
  isMini?: boolean;
};

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isMini',
})<StyledItemProps>(({ active, depth, isMini, theme }) => {
  const subItem = depth !== 1;

  const activeStyle = {
    color: theme.palette.primary.main,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
  };

  const activeSubStyle = {
    color: theme.palette.common.black,
    backgroundColor: isMini ? theme.palette.action.selected : 'inherit',
  };

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: isMini ? 'column' : 'row',
    gap: isMini ? theme.spacing(0) : theme.spacing(2),
    textTransform: 'capitalize',

    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius,
    height: 52,
    fill: active ? theme.palette.primary.main : theme.palette.text.secondary,

    // Sub item
    ...(subItem && {
      height: 36,
      ...(depth > 2 && {
        paddingLeft: theme.spacing(depth),
      }),
      ...(isMini && {
        height: 34,
        marginBottom: theme.spacing(0),
        '& .MuiListItemText-root': {
          width: '100%',
          marginBottom: theme.spacing(0),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
        },
      }),
    }),
    // Active item
    ...(active && {
      ...activeStyle,
      '&:hover': {
        ...activeStyle,
      },
    }),
    // Active sub item
    ...(subItem &&
      active && {
        ...activeSubStyle,
        '&:hover': {
          ...activeSubStyle,
        },
      }),
  };
});

type StyledItemIconProps = {
  isMini: boolean;
};

export const StyledIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isMini',
})<StyledItemIconProps>(({ isMini, theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    marginBottom: isMini ? theme.spacing(0.5) : theme.spacing(0),
    minWidth: 24,
    minHeight: 24,
  };
});

interface StyledListItemTextProps extends ListItemTextProps {
  active?: boolean;
  isMini?: boolean;
}

export const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isMini' && prop !== 'active',
})<StyledListItemTextProps>(({ active, isMini, theme }) => {
  return {
    display: 'flex',
    marginTop: isMini ? 0 : theme.spacing(0.5),
    '& .MuiTypography-root': {
      ...(active
        ? {
            ...(isMini
              ? { fontSize: '10px', fontWeight: '700', lineHeight: '1.5' }
              : { ...theme.typography.subtitle2 }),
          }
        : {
            ...(isMini
              ? { fontSize: '10px', fontWeight: '400', lineHeight: '1.5' }
              : { ...theme.typography.body2, lineHeight: '1.5' }),
          }),
    },
  };
});

export const StyledPopover = styled(Popover)(({ theme }) => ({
  pointerEvents: 'none',

  '& .MuiPopover-paper': {
    width: 160,
    pointerEvents: 'auto',
    boxShadow: theme.customShadows.dropdown,
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    ...bgBlur({ color: theme.palette.background.default }),
  },
}));
